import React, { useState, useEffect } from "react";
import {
  TextField,
  Select,
  MenuItem,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Grid,
  Typography,
  Snackbar,
  CircularProgress,
  TablePagination,
  Chip,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import DashboardAdmin from "../DashboardAdmin";
import useBDocTranslator from "../../hooks/useBDocTranslator";
import useUserState from "../../hooks/useUserState";
import axios from "axios";
import { format, parseISO } from "date-fns";
import { Alert } from "@material-ui/lab";
import { useHistory, useLocation } from "react-router-dom";
import FilterListIcon from "@material-ui/icons/FilterList";
import ClearIcon from "@material-ui/icons/Clear";
import GetAppIcon from "@material-ui/icons/GetApp";

const ORDER_TYPE_LABELS = {
  appointment: "Cita",
  affiliation: "Plan",
  driver_medical_certificate: "Dictamen Médico",
  unknown: "Desconocido",
};

const ORDER_TYPE_COLORS = {
  appointment: { bg: "#e3f2fd", color: "#1565c0" },
  affiliation: { bg: "#ede7f6", color: "#5e35b1" },
  driver_medical_certificate: { bg: "#e8f5e9", color: "#2e7d32" },
  unknown: { bg: "#eeeeee", color: "#424242" },
};

const OrderTypeChip = ({ orderType }) => {
  const style = ORDER_TYPE_COLORS[orderType] || ORDER_TYPE_COLORS.unknown;
  return (
    <Chip
      label={ORDER_TYPE_LABELS[orderType] || orderType}
      size="small"
      style={{ backgroundColor: style.bg, color: style.color, fontWeight: 600 }}
    />
  );
};

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
    marginTop: theme.spacing(3),
    overflowX: "auto",
  },
  paper: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  table: {
    minWidth: 650,
  },
  formControl: {
    margin: theme.spacing(1),
    minWidth: 120,
  },
  filterButtons: {
    display: "flex",
    justifyContent: "flex-end",
    marginTop: theme.spacing(2),
  },
  dialogPaper: {
    minWidth: "600px",
    maxWidth: "800px",
  },
  dialogContent: {
    padding: theme.spacing(3),
  },
  formField: {
    marginBottom: theme.spacing(2),
  },
  paymentDetails: {
    marginBottom: theme.spacing(3),
  },
  paymentDetailItem: {
    display: "flex",
    justifyContent: "space-between",
    marginBottom: theme.spacing(1),
  },
  dialogActions: {
    padding: theme.spacing(2, 3),
  },
}));

export default function OfflinePaymentsReport() {
  const classes = useStyles();
  const { t } = useBDocTranslator();
  const userState = useUserState();
  const [payments, setPayments] = useState([]);
  const [filteredPayments, setFilteredPayments] = useState([]);
  const [filters, setFilters] = useState({
    status: "",
    paymentMethod: "",
    startDate: "",
    endDate: "",
    conciliationStatus: "",
    orderType: "",
  });
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedAffiliation, setSelectedAffiliation] = useState(null);
  const history = useHistory();
  const location = useLocation();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [paymentMethods, setPaymentMethods] = useState([]);

  useEffect(() => {
    fetchPayments();
  }, []);

  // Deep link from the Driver Medical Certificate admin panel: auto-open a
  // specific offline payment's management dialog.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const offlinePaymentId = params.get("offlinePaymentId");
    if (offlinePaymentId && payments.length > 0) {
      const target = payments.find((p) => `${p.id}` === offlinePaymentId);
      if (target) {
        handleOpenDialog(target);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payments]);

  const fetchPayments = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL_BASE}/offlinePayments`,
        {
          headers: {
            Authorization: `Bearer ${userState[0].jwt}`,
          },
        }
      );
      setPayments(response.data);
      setFilteredPayments(response.data);

      // Extract unique payment methods
      const uniqueMethods = [];
      const methodIds = new Set();
      response.data.forEach(payment => {
        if (payment.payment_method && !methodIds.has(payment.payment_method.id)) {
          methodIds.add(payment.payment_method.id);
          uniqueMethods.push(payment.payment_method);
        }
      });
      setPaymentMethods(uniqueMethods);
    } catch (error) {
      console.error("Error fetching payments:", error);
      setError(t("Error fetching payments. Please try again."));
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (event) => {
    const { name, value } = event.target;
    const newFilters = { ...filters, [name]: value };
    setFilters(newFilters);
    // Auto-apply filters
    applyFiltersWithValues(newFilters);
    // Reset to first page when filters change
    setPage(0);
  };

  const applyFiltersWithValues = (filterValues) => {
    let filtered = payments;
    if (filterValues.status) {
      filtered = filtered.filter(
        (payment) => payment.concilation_status === filterValues.status
      );
    }
    if (filterValues.paymentMethod) {
      filtered = filtered.filter(
        (payment) =>
          payment.payment_method.id === parseInt(filterValues.paymentMethod)
      );
    }
    if (filterValues.startDate) {
      filtered = filtered.filter(
        (payment) => parseISO(payment.created_at) >= parseISO(filterValues.startDate)
      );
    }
    if (filterValues.endDate) {
      filtered = filtered.filter(
        (payment) => parseISO(payment.created_at) <= parseISO(filterValues.endDate)
      );
    }
    if (filters.orderType) {
      filtered = filtered.filter(
        (payment) => payment.order_type === filters.orderType
      );
    }
    setFilteredPayments(filtered);
  };

  const handleClearFilters = () => {
    const clearedFilters = {
      status: "",
      paymentMethod: "",
      startDate: "",
      endDate: "",
      conciliationStatus: "",
    };
    setFilters(clearedFilters);
    setFilteredPayments(payments);
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenDialog = async (payment) => {
    setSelectedPayment(payment);
    if (payment.affiliation_id && !payment.appointment_id) {
      try {
        const affiliationResponse = await axios.get(
          `${process.env.REACT_APP_API_URL_BASE}/affiliations/${payment.affiliation_id}`,
          {
            headers: {
              Authorization: `Bearer ${userState[0].jwt}`,
            },
          }
        );

        if (affiliationResponse.data) {
          const response = await axios.get(
            `${process.env.REACT_APP_API_URL_BASE}/affiliations/getPendingAffiliation`,
            {
              params: {
                patientId: affiliationResponse.data.patient_id,
                planId: affiliationResponse.data.plan_id,
              },
              headers: {
                Authorization: `Bearer ${userState[0].jwt}`,
              },
            }
          );
          setSelectedAffiliation(
            response.data?.id ? response.data : affiliationResponse.data
          );
        }
      } catch (error) {
        console.error("Error fetching affiliation:", error);
      }
    } else {
      setSelectedAffiliation(null);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleUpdateConciliationStatus = async () => {
    setLoading(true);
    setError(null);
    try {
      await axios.put(
        `${process.env.REACT_APP_API_URL_BASE}/offlinePayments/update`,
        {
          id: selectedPayment.id,
          concilation_status: selectedPayment.concilation_status,
          comments: selectedPayment.comments,
          updated_at: new Date(),
        },
        {
          headers: {
            Authorization: `Bearer ${userState[0].jwt}`,
          },
        }
      );
      await fetchPayments();
      handleCloseDialog();
    } catch (error) {
      console.error("Error updating conciliation status:", error);
      setError(t("Error updating conciliation status. Please try again."));
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    const headers = [
      "ID",
      "Patient Name",
      "Payment Method",
      "Amount",
      "Date",
      "Conciliation Status",
      "Reference",
      "Origin Reference",
      "Comments",
      "Appointment ID",
      "Affiliation ID",
    ];

    const csvData = [
      headers,
      ...filteredPayments.map((p) => [
        p.id,
        `${p.patient.firstName} ${p.patient.lastname}`,
        p.payment_method.name,
        p.amount,
        format(parseISO(p.created_at), "dd/MM/yyyy HH:mm"),
        p.concilation_status,
        p.reference,
        p.origin_reference,
        p.comments,
        p.appointment_id,
        p.affiliation_id,
      ]),
    ];

    const csvContent =
      "data:text/csv;charset=utf-8," +
      csvData.map((row) => row.join(",")).join("\n");

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "offline_payments_report.csv");
    document.body.appendChild(link);
    link.click();
  };

  return (
    <DashboardAdmin>
      <Typography variant="h4" gutterBottom>
        {t("Offline Payments Conciliation Report")}
      </Typography>
      <Paper className={classes.paper}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl className={classes.formControl} fullWidth>
              <InputLabel>{t("Conciliation Status")}</InputLabel>
              <Select
                name="status"
                value={filters.status}
                onChange={handleFilterChange}
              >
                <MenuItem value="">{t("All")}</MenuItem>
                <MenuItem value="in-review">{t("In Review")}</MenuItem>
                <MenuItem value="payed">{t("Payed")}</MenuItem>
                <MenuItem value="declined">{t("Declined")}</MenuItem>
                <MenuItem value="invalid">{t("Invalid")}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl className={classes.formControl} fullWidth>
              <InputLabel>{t("Payment Method")}</InputLabel>
              <Select
                name="paymentMethod"
                value={filters.paymentMethod}
                onChange={handleFilterChange}
              >
                <MenuItem value="">{t("All")}</MenuItem>
                {paymentMethods.map((method) => (
                  <MenuItem key={method.id} value={method.id}>
                    {method.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl className={classes.formControl} fullWidth>
              <InputLabel>{t("Order Type")}</InputLabel>
              <Select
                name="orderType"
                value={filters.orderType}
                onChange={handleFilterChange}
              >
                <MenuItem value="">{t("All")}</MenuItem>
                <MenuItem value="appointment">{t("Cita")}</MenuItem>
                <MenuItem value="affiliation">{t("Plan")}</MenuItem>
                <MenuItem value="driver_medical_certificate">
                  {t("Dictamen Médico")}
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              name="startDate"
              label={t("Start Date")}
              type="date"
              value={filters.startDate}
              onChange={handleFilterChange}
              InputLabelProps={{
                shrink: true,
              }}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              name="endDate"
              label={t("End Date")}
              type="date"
              value={filters.endDate}
              onChange={handleFilterChange}
              InputLabelProps={{
                shrink: true,
              }}
              fullWidth
            />
          </Grid>
        </Grid>
        <div className={classes.filterButtons}>
          <Button
            variant="outlined"
            color="default"
            onClick={handleClearFilters}
            startIcon={<ClearIcon />}
            style={{ marginRight: "10px" }}
          >
            {t("Clear Filters")}
          </Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={handleDownload}
            startIcon={<GetAppIcon />}
          >
            {t("Download CSV")}
          </Button>
        </div>
      </Paper>
      <TableContainer component={Paper}>
        <Table className={classes.table}>
          <TableHead>
            <TableRow>
              <TableCell>{t("ID")}</TableCell>
              <TableCell>{t("Patient")}</TableCell>
              <TableCell>{t("Payment Method")}</TableCell>
              <TableCell>{t("Amount")}</TableCell>
              <TableCell>{t("Date")}</TableCell>
              <TableCell>{t("Order Type")}</TableCell>
              <TableCell>{t("Conciliation Status")}</TableCell>
              <TableCell>{t("Actions")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredPayments.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography variant="body1" color="textSecondary" style={{ padding: "20px" }}>
                    {loading ? t("Loading...") : t("No payments found matching the current filters.")}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredPayments
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((payment) => (
                  <TableRow key={payment.id}>
                    <TableCell>{payment.id}</TableCell>
                    <TableCell>{`${payment.patient.firstName} ${payment.patient.lastname}`}</TableCell>
                    <TableCell>{payment.payment_method.name}</TableCell>
                    <TableCell>{payment.amount}</TableCell>
                    <TableCell>
                      {format(new Date(payment.created_at), "dd/MM/yyyy HH:mm")}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={payment.concilation_status}
                        color={
                          payment.concilation_status === "payed" ? "primary" :
                            payment.concilation_status === "in-review" ? "default" :
                              payment.concilation_status === "declined" ? "secondary" : "default"
                        }
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={() => handleOpenDialog(payment)}
                      >
                        {t("Manage")}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 25, 50, 100]}
        component="div"
        count={filteredPayments.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onChangePage={handleChangePage}
        onChangeRowsPerPage={handleChangeRowsPerPage}
        labelRowsPerPage={t("Rows per page:")}
      />
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        classes={{ paper: classes.dialogPaper }}
      >
        <DialogTitle>{t("Manage Offline Payment")}</DialogTitle>
        <DialogContent className={classes.dialogContent}>
          {selectedPayment && (
            <div>
              <div className={classes.paymentDetails}>
                <Typography variant="h6" gutterBottom>
                  {t("Payment Details")}
                </Typography>
                <div className={classes.paymentDetailItem}>
                  <Typography variant="body1">{t("ID")}:</Typography>
                  <Typography variant="body1">{selectedPayment.id}</Typography>
                </div>
                <div className={classes.paymentDetailItem}>
                  <Typography variant="body1">{t("Patient")}:</Typography>
                  <Typography variant="body1">
                    {`${selectedPayment.patient.firstName} ${selectedPayment.patient.lastname}`}
                  </Typography>
                </div>
                <div className={classes.paymentDetailItem}>
                  <Typography variant="body1">{t("Amount")}:</Typography>
                  <Typography variant="body1">
                    {selectedPayment.amount}
                  </Typography>
                </div>
                <div className={classes.paymentDetailItem}>
                  <Typography variant="body1">{t("Date")}:</Typography>
                  <Typography variant="body1">
                    {format(
                      parseISO(selectedPayment.created_at),
                      "dd/MM/yyyy HH:mm"
                    )}
                  </Typography>
                </div>
                <div className={classes.paymentDetailItem}>
                  <Typography variant="body1">
                    {t("Payment Method")}:
                  </Typography>
                  <Typography variant="body1">
                    {selectedPayment.payment_method.name}
                  </Typography>
                </div>
                <div className={classes.paymentDetailItem}>
                  <Typography variant="body1">{t("Reference")}:</Typography>
                  <Typography variant="body1">
                    {selectedPayment.reference || t("N/A")}
                  </Typography>
                </div>
                <div className={classes.paymentDetailItem}>
                  <Typography variant="body1">
                    {t("Appointment ID")}:
                  </Typography>
                  <Typography variant="body1">
                    {selectedPayment.appointment_id || t("N/A")}
                  </Typography>
                </div>
                {selectedPayment.driver_medical_certificate_request && (
                  <>
                    <Typography
                      variant="h6"
                      gutterBottom
                      style={{ marginTop: "20px" }}
                    >
                      {t("Dictamen Médico")}
                    </Typography>
                    <div className={classes.paymentDetailItem}>
                      <Typography variant="body1">{t("Titular")}:</Typography>
                      <Typography variant="body1">
                        {selectedPayment.driver_medical_certificate_request
                          .full_name}
                      </Typography>
                    </div>
                    <div className={classes.paymentDetailItem}>
                      <Typography variant="body1">{t("Cédula")}:</Typography>
                      <Typography variant="body1">
                        {
                          selectedPayment.driver_medical_certificate_request
                            .document_number
                        }
                      </Typography>
                    </div>
                    <div className={classes.paymentDetailItem}>
                      <Typography variant="body1">
                        {t("Tipo de licencia")}:
                      </Typography>
                      <Typography variant="body1">
                        {
                          selectedPayment.driver_medical_certificate_request
                            .license_type
                        }
                      </Typography>
                    </div>
                    <div className={classes.paymentDetailItem}>
                      <Typography variant="body1">
                        {t("Estado de la solicitud")}:
                      </Typography>
                      <Typography variant="body1">
                        {selectedPayment.driver_medical_certificate_request.status}
                      </Typography>
                    </div>
                    <Button
                      color="primary"
                      onClick={() =>
                        history.push(
                          `/users/driver-medical-certificate/requests?highlight=${selectedPayment.driver_medical_certificate_request.id}`
                        )
                      }
                    >
                      {t("Ver en Dictámenes Médicos")}
                    </Button>
                  </>
                )}
                {selectedAffiliation && !selectedPayment.appointment_id && (
                  <>
                    <Typography
                      variant="h6"
                      gutterBottom
                      style={{ marginTop: "20px" }}
                    >
                      {t("Affiliation Details")}
                    </Typography>
                    <div className={classes.paymentDetailItem}>
                      <Typography variant="body1">{t("Plan")}:</Typography>
                      <Typography variant="body1">
                        {selectedAffiliation.plan?.name || t("N/A")}
                      </Typography>
                    </div>
                    <div className={classes.paymentDetailItem}>
                      <Typography variant="body1">{t("Status")}:</Typography>
                      <Typography
                        variant="body1"
                        style={{
                          color:
                            selectedAffiliation.status === "active"
                              ? "green"
                              : "inherit",
                        }}
                      >
                        {selectedAffiliation.status}
                        {selectedAffiliation.status === "active" &&
                          selectedPayment.concilation_status === "payed" && (
                            <span
                              style={{
                                marginLeft: "8px",
                                fontSize: "0.8em",
                                color: "gray",
                              }}
                            >
                              ({t("Payment verified")})
                            </span>
                          )}
                      </Typography>
                    </div>
                    <div className={classes.paymentDetailItem}>
                      <Typography variant="body1">
                        {t("Start Date")}:
                      </Typography>
                      <Typography variant="body1">
                        {format(
                          parseISO(selectedAffiliation.affiliation_dt_start),
                          "dd/MM/yyyy"
                        )}
                      </Typography>
                    </div>
                    {selectedAffiliation.affiliation_dt_end && (
                      <div className={classes.paymentDetailItem}>
                        <Typography variant="body1">
                          {t("End Date")}:
                        </Typography>
                        <Typography variant="body1">
                          {format(
                            parseISO(selectedAffiliation.affiliation_dt_end),
                            "dd/MM/yyyy"
                          )}
                        </Typography>
                      </div>
                    )}
                  </>
                )}
              </div>

              <FormControl className={classes.formField} fullWidth>
                <InputLabel>{t("Conciliation Status")}</InputLabel>
                <Select
                  value={selectedPayment.concilation_status}
                  onChange={(e) => {
                    setSelectedPayment({
                      ...selectedPayment,
                      concilation_status: e.target.value,
                    });
                    // If payment is marked as paid, update affiliation status to active
                    if (e.target.value === "payed" && selectedAffiliation) {
                      setSelectedAffiliation({
                        ...selectedAffiliation,
                        status: "active",
                      });
                    }
                  }}
                  disabled={loading}
                >
                  <MenuItem value="in-review">{t("In Review")}</MenuItem>
                  <MenuItem value="payed">{t("Payed")}</MenuItem>
                  <MenuItem value="declined">{t("Declined")}</MenuItem>
                  <MenuItem value="invalid">{t("Invalid")}</MenuItem>
                </Select>
              </FormControl>

              <TextField
                className={classes.formField}
                label={t("Comments")}
                multiline
                rows={4}
                fullWidth
                variant="outlined"
                value={selectedPayment.comments || ""}
                onChange={(e) =>
                  setSelectedPayment({
                    ...selectedPayment,
                    comments: e.target.value,
                  })
                }
              />
            </div>
          )}
        </DialogContent>
        <DialogActions className={classes.dialogActions}>
          <Button onClick={handleCloseDialog} color="primary">
            {t("Cancel")}
          </Button>
          <Button
            onClick={handleUpdateConciliationStatus}
            color="primary"
            variant="contained"
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : t("Update")}
          </Button>
        </DialogActions>
      </Dialog>
      {loading && (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            margin: "20px 0",
          }}
        >
          <CircularProgress />
        </div>
      )}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
      >
        <Alert onClose={() => setError(null)} severity="error">
          {error}
        </Alert>
      </Snackbar>
    </DashboardAdmin>
  );
}
